// client/src/components/common/Navbar.js
// ================================================================
// WHOLESALEHUB NAVBAR — REDESIGNED
// Clean grouped navigation with "More" dropdown for secondary items
// UPDATED: Added Catalog Discounts to Sales Tools, renamed Discounts → Customer Discounts
// ================================================================

import React, { useState, useRef, useEffect } from 'react';
import {
  ShoppingCart, User, Heart, FileText, Upload, CreditCard,
  Percent, BookOpen, HelpCircle, BarChart, Lock, Zap,
  Package, ChevronDown, FileStack, Settings, Tag, Users, DollarSign
} from 'lucide-react';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import MobileNavbar from '../mobile/MobileNavbar';
import MobileMenu from '../mobile/MobileMenu';
import NotificationBell from './NotificationBell';
import SearchBar from '../search/SearchBar';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from 'react-i18next';

// ── Reusable nav link component ──
const NavLink = ({ onClick, active, children, icon: Icon, accent }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
      active
        ? 'bg-blue-50 text-blue-700'
        : accent
        ? `text-${accent}-600 hover:bg-${accent}-50`
        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
    }`}
  >
    {Icon && <Icon className={`w-4 h-4 ${active ? 'text-blue-600' : accent ? `text-${accent}-500` : ''}`} />}
    {children}
  </button>
);

// ── Dropdown menu component ──
const DropdownMenu = ({ label, icon: Icon, items, currentPath }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isChildActive = items.some(item => currentPath === item.path);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
          isChildActive
            ? 'bg-blue-50 text-blue-700'
            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
        }`}
      >
        {Icon && <Icon className="w-4 h-4" />}
        {label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-52 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          {items.map((item, idx) => (
            <button
              key={idx}
              onClick={() => { item.onClick(); setOpen(false); }}
              className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors ${
                currentPath === item.path
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {item.icon && <item.icon className={`w-4 h-4 ${item.accent ? `text-${item.accent}-500` : 'text-gray-400'}`} />}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ================================================================
// MAIN NAVBAR
// ================================================================
const Navbar = () => {
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { items } = useCart();
  const isMobile = useMediaQuery('(max-width: 1023px)');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  const cartItemCount = items?.reduce((total, item) => total + item.quantity, 0) || 0;
  const currentPath = location.pathname;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleLogoClick = () => {
    if (isAuthenticated && user) {
      const routes = { business: '/dashboard', wholesaler: '/wholesaler/dashboard', admin: '/admin/dashboard' };
      navigate(routes[user.role] || '/');
    } else {
      navigate('/');
    }
  };

  // Mobile view
  if (isMobile) {
    return (
      <>
        <MobileNavbar
          user={user}
          cartItemCount={cartItemCount}
          onMenuClick={() => setIsMobileMenuOpen(true)}
          onCartClick={() => navigate('/cart')}
        />
        <MobileMenu
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
          user={user}
          currentPath={currentPath}
        />
      </>
    );
  }

  // ================================================================
  // DESKTOP
  // ================================================================
  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          
          {/* ── Logo ── */}
          <button
            onClick={handleLogoClick}
            className="text-xl font-bold text-blue-600 hover:text-blue-700 transition-colors flex-shrink-0 mr-6"
          >
            WholesaleHub
          </button>

          {/* ── SEARCH BAR (Business only) ── */}
          {isAuthenticated && user?.role === 'business' && (
            <div className="flex-shrink-0 mr-4">
              <SearchBar />
            </div>
          )}

          {/* ── CENTER: Navigation Links ── */}
          <div className="flex-1 flex items-center justify-center">

            {/* ═══ BUSINESS NAV ═══ */}
            {isAuthenticated && user?.role === 'business' && (
              <div className="flex items-center gap-1">
                <NavLink onClick={() => navigate('/dashboard')} active={currentPath === '/dashboard'}>
                  {t('common:navigation.dashboard')}
                </NavLink>
                <NavLink onClick={() => navigate('/wholesalers')} active={currentPath === '/wholesalers' || currentPath.startsWith('/wholesalers/')}>
                  {t('common:navigation.products')}
                </NavLink>
                <NavLink onClick={() => navigate('/my-orders')} active={currentPath === '/my-orders'}>
                  {t('common:navigation.orders')}
                </NavLink>
                <NavLink onClick={() => navigate('/business/favorites')} active={currentPath === '/business/favorites'} icon={Heart}>
                  {t('common:navigation.favorites')}
                </NavLink>
                <NavLink onClick={() => navigate('/business/templates')} active={currentPath === '/business/templates'} icon={FileStack}>
                  {t('common:navigation.templates')}
                </NavLink>
                <NavLink onClick={() => navigate('/offers')} active={currentPath === '/offers'} icon={Zap} accent="red">
                  {t('common:navigation.offers', 'Offers')}
                </NavLink>
              </div>
            )}

            {/* ═══ WHOLESALER NAV ═══ */}
            {isAuthenticated && user?.role === 'wholesaler' && (() => {
              const subRole = user?.teamContext?.subRole || 'manager';
              const isManager = subRole === 'manager';
              const isOffice = subRole === 'office';
              const isSalesRep = subRole === 'sales_rep';
              return (
                <div className="flex items-center gap-1">
                  <NavLink onClick={() => navigate('/wholesaler/dashboard')} active={currentPath === '/wholesaler/dashboard'}>
                    {t('common:navigation.dashboard')}
                  </NavLink>

                  {/* Products dropdown — Manager + Office only */}
                  {(isManager || isOffice) && (
                    <DropdownMenu
                      label={t('common:navigation.products')}
                      icon={Package}
                      currentPath={currentPath}
                      items={[
                        { label: t('common:navigation.productList', 'Product List'), icon: Package, path: '/wholesaler/products', onClick: () => navigate('/wholesaler/products') },
                        { label: t('common:navigation.bulkUpload'), icon: Upload, path: '/wholesaler/products/bulk-upload', onClick: () => navigate('/wholesaler/products/bulk-upload') },
                      ]}
                    />
                  )}

                  <NavLink onClick={() => navigate('/wholesaler/orders')} active={currentPath === '/wholesaler/orders'}>
                    {t('common:navigation.orders')}
                  </NavLink>

                  {/* Place Order — Manager + Sales Rep (on-behalf ordering) */}
                  {(isManager || isSalesRep) && (
                    <NavLink onClick={() => navigate('/wholesaler/place-order')} active={currentPath === '/wholesaler/place-order'} icon={ShoppingCart}>
                      {t('common:navigation.placeOrder', 'Place Order')}
                    </NavLink>
                  )}

                  {/* Sales Tools dropdown — Manager + Office only */}
                  {(isManager || isOffice) && (
                    <DropdownMenu
                      label={t('common:navigation.salesTools', 'Sales Tools')}
                      icon={Zap}
                      currentPath={currentPath}
                      items={[
                        { label: t('common:navigation.flashOffers', 'Flash Offers'), icon: Zap, accent: 'red', path: '/wholesaler/flash-offers', onClick: () => navigate('/wholesaler/flash-offers') },
                        { label: t('common:navigation.catalogDiscounts', 'Catalog Discounts'), icon: Tag, accent: 'teal', path: '/wholesaler/catalog-discounts', onClick: () => navigate('/wholesaler/catalog-discounts') },
                        { label: t('common:navigation.customerDiscounts', 'Customer Discounts'), icon: Percent, accent: 'orange', path: '/wholesaler/discounts', onClick: () => navigate('/wholesaler/discounts') },
                        { label: t('common:navigation.productDiscounts', 'Product Discounts'), icon: Tag, accent: 'purple', path: '/wholesaler/product-discounts', onClick: () => navigate('/wholesaler/product-discounts') },
                        { label: t('common:navigation.catalogRequests', 'Catalog Requests'), icon: Lock, accent: 'yellow', path: '/wholesaler/catalog-requests', onClick: () => navigate('/wholesaler/catalog-requests') },
                      ]}
                    />
                  )}

                  {/* Subscription — Manager only */}
                  {isManager && (
                    <NavLink onClick={() => navigate('/wholesaler/subscription')} active={currentPath === '/wholesaler/subscription'} icon={CreditCard}>
                      {t('common:navigation.subscription', 'Subscription')}
                    </NavLink>
                  )}

                  {/* Team — Manager only */}
                  {isManager && (
                    <NavLink onClick={() => navigate('/wholesaler/team')} active={currentPath === '/wholesaler/team'} icon={Users}>
                      {t('common:navigation.team', 'Team')}
                    </NavLink>
                  )}
                </div>
              );
            })()}

            {/* ═══ ADMIN NAV ═══ */}
            {isAuthenticated && user?.role === 'admin' && (
              <div className="flex items-center gap-1">
                <NavLink onClick={() => navigate('/admin/dashboard')} active={currentPath === '/admin/dashboard'}>
                  {t('common:navigation.dashboard')}
                </NavLink>
                <NavLink onClick={() => navigate('/admin/users')} active={currentPath === '/admin/users' || currentPath.startsWith('/admin/wholesaler') || currentPath.startsWith('/admin/business')}>
                  {t('common:navigation.users')}
                </NavLink>
                <NavLink onClick={() => navigate('/admin/orders')} active={currentPath === '/admin/orders'}>
                  {t('common:navigation.orders')}
                </NavLink>
                <NavLink onClick={() => navigate('/admin/billing')} active={currentPath === '/admin/billing'} icon={CreditCard}>
                  {t('common:navigation.billing', 'Billing')}
                </NavLink>
                <NavLink onClick={() => navigate('/admin/tools')} active={currentPath === '/admin/tools'}>
                  {t('common:navigation.tools', 'Tools')}
                </NavLink>
              </div>
            )}

            {/* ═══ NOT AUTHENTICATED ═══ */}
            {!isAuthenticated && (
              <div className="flex items-center gap-1">
                <NavLink onClick={() => navigate('/wholesalers')}>
                  {t('common:navigation.browseWholesalers', 'Browse Wholesalers')}
                </NavLink>
                <NavLink onClick={() => navigate('/pricing')} active={currentPath === '/pricing'} icon={DollarSign} accent="green">
                  {t('common:navigation.pricing', 'Pricing')}
                </NavLink>
                <NavLink onClick={() => navigate('/help')} icon={BookOpen}>
                  {t('common:navigation.help')}
                </NavLink>
                <NavLink onClick={() => navigate('/faq')} icon={HelpCircle}>
                  {t('common:navigation.faq')}
                </NavLink>
              </div>
            )}
          </div>

          {/* ── RIGHT SIDE: Actions ── */}
          <div className="flex items-center gap-2 flex-shrink-0 ml-4">
            <LanguageSwitcher />

            {isAuthenticated ? (
              <>
                {/* Cart (Business only) */}
                {user?.role === 'business' && (
                  <button
                    onClick={() => navigate('/cart')}
                    className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    title={t('common:navigation.cart')}
                  >
                    <ShoppingCart className="w-5 h-5" />
                    {cartItemCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full w-4.5 h-4.5 min-w-[18px] flex items-center justify-center">
                        {cartItemCount > 9 ? '9+' : cartItemCount}
                      </span>
                    )}
                  </button>
                )}

                {/* Notifications */}
                <NotificationBell />

                {/* Profile */}
                <div className="relative" ref={profileRef}>
                  <button
                    onClick={() => setProfileOpen(!profileOpen)}
                    className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-sm font-medium text-gray-700 hidden xl:block max-w-[120px] truncate">
                      {user?.profile?.businessName || user?.profile?.companyName || user?.email?.split('@')[0] || 'User'}
                    </span>
                    <ChevronDown className={`w-3.5 h-3.5 text-gray-400 hidden xl:block transition-transform ${profileOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {profileOpen && (
                    <div className="absolute right-0 mt-1 w-52 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                      <div className="px-4 py-2.5 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {user?.profile?.businessName || user?.profile?.companyName || 'User'}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                      </div>
                      
                      <button
                        onClick={() => {
                          navigate(user?.role === 'business' ? '/business/profile' : user?.role === 'wholesaler' ? '/wholesaler/profile' : '/admin/settings');
                          setProfileOpen(false);
                        }}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <User className="w-4 h-4 text-gray-400" />
                        {t('common:navigation.profile')}
                      </button>
                      
                      {user?.role !== 'admin' && (
                        <button
                          onClick={() => {
                            navigate(user?.role === 'business' ? '/business/analytics' : '/wholesaler/analytics');
                            setProfileOpen(false);
                          }}
                          className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <BarChart className="w-4 h-4 text-gray-400" />
                          {t('common:navigation.analytics')}
                        </button>
                      )}

                      <div className="border-t border-gray-100 my-1" />
                      
                      <button onClick={() => { navigate('/pricing'); setProfileOpen(false); }}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50">
                        <DollarSign className="w-4 h-4 text-gray-400" />
                        {t('common:navigation.pricing', 'Pricing')}
                      </button>
                      <button onClick={() => { navigate('/help'); setProfileOpen(false); }}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50">
                        <BookOpen className="w-4 h-4 text-gray-400" />
                        {t('common:navigation.help')}
                      </button>
                      <button onClick={() => { navigate('/faq'); setProfileOpen(false); }}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50">
                        <HelpCircle className="w-4 h-4 text-gray-400" />
                        {t('common:navigation.faq')}
                      </button>

                      <div className="border-t border-gray-100 my-1" />
                      
                      <button onClick={() => { handleLogout(); setProfileOpen(false); }}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50">
                        <Settings className="w-4 h-4 text-red-400" />
                        {t('common:navigation.logout')}
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <button onClick={() => navigate('/login')}
                  className="px-4 py-2 text-sm text-gray-700 hover:text-blue-600 font-medium transition-colors">
                  {t('common:navigation.login')}
                </button>
                <button onClick={() => navigate('/register')}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors">
                  {t('common:navigation.register')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;